import { generateShortId } from '@o2d/shared';
import { creationDefaults } from '../../core/viewport/zoom-cluster.service.js';
import type { PersonObject } from './person.interface.js';
import { DEFAULT_PERSON_ICON, type PersonIcon } from './person-icons.config.js';
import { PERSON_FONT_SIZE, PERSON_DEFAULT_FONT_WEIGHT } from './person.types.js';

const DEFAULT_COLOR = '#374151';
const DEFAULT_TEXT = 'Name';

/**
 * Freeze the payload in DEV builds so accidental mutations surface as a
 * TypeError instead of silently corrupting the PersonObject contract.
 */
function freezeInDev<T extends object>(value: T): T {
  if (import.meta.env?.DEV) {
    return Object.freeze(value);
  }
  return value;
}

/**
 * Stefan, 2026-09-03 — "choose properties before drawing": optional overrides
 * for the pre-placement-staged options-circle values. Covers every stageable
 * intent `resolvePersonProperty` maps (`person-options.config.ts`) except the
 * base label `color`, which has no options-circle intent of its own — `fill`
 * maps to the separate `circleFill` avatar-background field instead.
 *
 * Bugreport (Anke, 2026-09-03): "bei der person... ist fast nichts korrekt
 * angeschlossen" — only `icon` was ever wired; every other field below was
 * completely missing.
 */
export interface CreatePersonDefaultsOptions {
  readonly icon?: PersonIcon;
  readonly circleFill?: string;
  readonly circleStroke?: string;
  readonly circleStrokeWidth?: number;
  readonly circleStrokeStyle?: 'solid' | 'dashed' | 'dotted';
  readonly backgroundOpacity?: number;
  readonly boxShadow?: 'on';
  readonly textColor?: string;
  readonly fontSize?: number;
  readonly fontWeight?: number;
  readonly fontStyle?: 'italic';
  readonly textDecoration?: 'underline' | 'line-through';
}

/**
 * Build a fresh person default payload — extracted from the inline
 * `placePersonAt` body in `o2d-whiteboard.element.ts` per PRD-F2
 * §TR-Defaults-Factory Person.
 *
 * - `sizeOpt === null`: bare-click branch, person is centred on the pointer
 *   (top-left = point - size/2). Dimensions scale inversely with viewport
 *   scale via `creationDefaults`.
 * - `sizeOpt !== null`: drag-to-create branch, the dragged box is the exact
 *   placement rectangle (top-left = pointer).
 */
export function createPersonDefaults(
  viewportScale: number,
  point: { x: number; y: number },
  sizeOpt: { width: number; height: number } | null,
  options?: CreatePersonDefaultsOptions,
): PersonObject {
  const cd = creationDefaults(viewportScale);
  const w = sizeOpt ? sizeOpt.width : cd.personWidth;
  const h = sizeOpt ? sizeOpt.height : cd.personHeight;
  const x = sizeOpt ? point.x : point.x - w / 2;
  const y = sizeOpt ? point.y : point.y - h / 2;
  const person: PersonObject = {
    id: generateShortId(),
    type: 'person',
    x,
    y,
    width: w,
    height: h,
    rotation: 0,
    text: DEFAULT_TEXT,
    color: DEFAULT_COLOR,
    icon: options?.icon ?? DEFAULT_PERSON_ICON,
    // Seeded (rather than left undefined) so the size/weight slots in the
    // options circle light up from the start. The values equal the renderer's
    // fallbacks, so a seeded person and a persisted pre-feature one look alike.
    fontSize: options?.fontSize ?? PERSON_FONT_SIZE,
    fontWeight: options?.fontWeight ?? PERSON_DEFAULT_FONT_WEIGHT,
    // The rest have NO hardcoded default (unset = renderer falls back on its
    // own) — conditional-spread only, so an untouched field stays truly
    // unset rather than being overwritten with `undefined`.
    ...(options?.circleFill !== undefined ? { circleFill: options.circleFill } : {}),
    ...(options?.circleStroke !== undefined ? { circleStroke: options.circleStroke } : {}),
    ...(options?.circleStrokeWidth !== undefined ? { circleStrokeWidth: options.circleStrokeWidth } : {}),
    ...(options?.circleStrokeStyle !== undefined ? { circleStrokeStyle: options.circleStrokeStyle } : {}),
    ...(options?.backgroundOpacity !== undefined ? { backgroundOpacity: options.backgroundOpacity } : {}),
    ...(options?.boxShadow !== undefined ? { boxShadow: options.boxShadow } : {}),
    ...(options?.textColor !== undefined ? { textColor: options.textColor } : {}),
    ...(options?.fontStyle !== undefined ? { fontStyle: options.fontStyle } : {}),
    ...(options?.textDecoration !== undefined ? { textDecoration: options.textDecoration } : {}),
  };
  return freezeInDev(person);
}
